import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';

import { decodeFilters, encodeFilters } from '@/lib/urlUtils';

interface SetSearchParamFiltersOptions {
    /** Replace the current history entry, for per-keystroke search input. */
    replace?: boolean;
}

export interface UseSearchParamFiltersReturn<T> {
    filters: T;
    setFilters: (
        patch: Partial<T>,
        options?: SetSearchParamFiltersOptions,
    ) => void;
}

/**
 * A list's search/filter state as one encoded URL search key, restored with
 * `?page` when the user comes back to the list.
 *
 * `setFilters` merges `patch`, clears `page` (it only applies to the filters it
 * was paginated under) and updates `filters` straight away, so a search box
 * bound to it never lags the user's typing. `defaults` must be a stable
 * (module-level) object.
 */
export function useSearchParamFilters<T extends object>(
    key: string,
    defaults: T,
): UseSearchParamFiltersReturn<T> {
    const search = useSearch({ strict: false }) as Record<string, unknown>;
    const navigate = useNavigate();
    const urlRaw = typeof search[key] === 'string' ? search[key] : undefined;

    // A local copy that runs ahead of the URL while our writes are in flight.
    const [raw, setRaw] = useState(urlRaw);
    const rawRef = useRef(raw);
    // Values we've written that the URL hasn't caught up with yet. `navigate()`
    // is async, so an older write can land after a newer one; copying it back
    // would make a search box jump back.
    const pendingWritesRef = useRef<(string | undefined)[]>([]);

    // Follow the URL only for changes we didn't make (Back/Forward, a restore).
    useEffect(() => {
        const pending = pendingWritesRef.current;
        const ownWriteIndex = pending.indexOf(urlRaw);
        if (ownWriteIndex !== -1) {
            pendingWritesRef.current = pending.slice(ownWriteIndex + 1);
            return;
        }
        pendingWritesRef.current = [];
        rawRef.current = urlRaw;
        setRaw(urlRaw);
    }, [urlRaw]);

    const filters = useMemo(
        () => decodeFilters(raw, defaults),
        [raw, defaults],
    );

    const setFilters = useCallback(
        (patch: Partial<T>, { replace }: SetSearchParamFiltersOptions = {}) => {
            const next = encodeFilters(
                { ...decodeFilters(rawRef.current, defaults), ...patch },
                defaults,
            );
            rawRef.current = next;
            setRaw(next);
            pendingWritesRef.current.push(next);
            void navigate({
                search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    [key]: next,
                    page: undefined,
                }),
                replace,
            } as Parameters<typeof navigate>[0]);
        },
        [navigate, key, defaults],
    );

    return { filters, setFilters };
}
